/**
 * Manage Parent Accounts Page
 * View and manage parent accounts linked to a student
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useNotification } from '@/hooks/useNotification';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { getStudentParents, type Parent } from '@/api/parents';
import { getStudentById } from '@/api/students';

export const ManageParentAccountsPage = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  const [parents, setParents] = useState<Parent[]>([]);
  const [studentName, setStudentName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (studentId) {
      fetchData();
    }
  }, [studentId]);

  const fetchData = async () => {
    if (!studentId) return;
    
    try {
      setIsLoading(true);
      
      // Fetch student details
      const studentResponse = await getStudentById(studentId);
      if (studentResponse.success && (studentResponse as any).student) {
        const student = (studentResponse as any).student;
        setStudentName(`${student.firstName} ${student.lastName}`);
      }
      
      // Fetch parent accounts
      const parentsResponse = await getStudentParents(studentId);
      if (parentsResponse.success && (parentsResponse as any).parents) {
        setParents((parentsResponse as any).parents);
      } else {
        setParents([]);
      }
    } catch (error) {
      console.error('Failed to fetch parent accounts:', error);
      showNotification('error', 'Failed to load parent accounts');
      setParents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Parent Accounts</h1>
          <p className="text-slate-600 mt-2">
            Manage parent accounts for {studentName}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/students/${studentId}`)}>
          ← Back to Student
        </Button>
      </div>

      {/* Parent Accounts List */}
      <Card title="Linked Parent Accounts">
        <div className="p-6">
          {parents.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-slate-400 mb-4">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                No parent accounts linked yet
              </h3>
              <p className="text-slate-600 mb-6">
                Create a parent account to allow parents to view their child's stories and progress.
              </p>
              <Button
                variant="primary"
                onClick={() => navigate(`/students/${studentId}/parents/new`)}
              >
                Add Parent Account
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-4 mb-6">
                {parents.map((parent) => (
                  <div
                    key={parent.parentId}
                    className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-900">
                        {parent.parentName}
                      </h3>
                      <p className="text-sm text-slate-600">{parent.email}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Linked on {formatDate(parent.linkedDate)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-full">
                        Active
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <Button
                variant="primary"
                onClick={() => navigate(`/students/${studentId}/parents/new`)}
                className="w-full"
              >
                Add Another Parent Account
              </Button>
            </>
          )}
        </div>
      </Card>

      {/* Info Card */}
      <Card>
        <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">
            ℹ️ About Parent Accounts
          </h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Parents can view their child's stories and progress</li>
            <li>• Parents cannot edit student information or settings</li>
            <li>• Multiple parents can be linked to one student</li>
            <li>• Parents log in using their email and password</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};
